import { fetchRoomsForOffice, type MicrosoftRoomMapping } from '../api'

export type Room = {
    id: string
    name: string
    details: string
    capacity: number
    thumb: string | null
    thumbKey: string | null
    tagIds: string[]
    outlookRoomId: string | null
    outlookRoomName: string | null
    outlookRoomEmail: string | null
}

export type RoomGroup = {
    id: string
    title: string
    collapsed: boolean
    rooms: Room[]
}

export type OfficeOption = {
    id: string
    name: string
    hours: string
    timeZone: string
}

export type RoomFilterTag = {
    id: string
    name: string
}

export type CalendarEvent = {
    id: string
    startTime: string
    endTime: string
    reservableID?: string | null
}

const supportedThumbKeys = new Set(['barcelona', 'lisabon', 'lissabon', 'new-york', 'paris', 'london', 'riga'])

export function normalize(value: string) {
    return value.toLowerCase().trim()
}

export function formatTime(date: Date | null) {
    if (!date) {
        return '--:--'
    }

    return new Intl.DateTimeFormat(undefined, {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    }).format(date)
}

function buildRoomDetails(tagNames: string[]) {
    return tagNames.join(', ') || 'No amenities'
}

function pickMainImage(
    images: Array<{
        location?: string | null
        isMain?: boolean | null
    } | null> = [],
) {
    const mainImage = images.find((image) => image?.isMain && image.location)
    const fallbackImage = images.find((image) => image?.location)
    return mainImage?.location || fallbackImage?.location || null
}

function getFallbackThumbKey(name: string) {
    const normalizedName = normalize(name).replace(/\s+/g, '-')

    if (!supportedThumbKeys.has(normalizedName)) {
        return null
    }

    return normalizedName === 'lisabon' ? 'lisbon' : normalizedName
}

export function mapSpacesToGroups(
    spaces: Awaited<ReturnType<typeof fetchRoomsForOffice>>['spaces'],
    roomMappings: Map<string, MicrosoftRoomMapping>,
): RoomGroup[] {
    return spaces.map((space) => {
        const rooms = (space.Reservables?.items || [])
            .filter((room): room is NonNullable<typeof room> => Boolean(room?.id))
            .map((room) => {
                const tags = (room.Tags?.items || [])
                    .map((item) => item?.Tag)
                    .filter((tag): tag is { id?: string | null; name?: string | null } => Boolean(tag?.name))
                const mapping = roomMappings.get(room.id)

                if (!mapping?.roomEmail) {
                    return null
                }

                return {
                    id: room.id,
                    name: room.name || 'Unnamed room',
                    details: buildRoomDetails(tags.map((tag) => tag.name || '').filter(Boolean)),
                    capacity: room.maxAmountOfPeople || 0,
                    thumb: pickMainImage(room.ReservableImages?.items || []),
                    thumbKey: getFallbackThumbKey(room.name || ''),
                    tagIds: tags.map((tag) => tag.id || '').filter(Boolean),
                    outlookRoomId: mapping.roomId || null,
                    outlookRoomName: mapping.roomName || null,
                    outlookRoomEmail: mapping.roomEmail || null,
                }
            })
            .filter((room): room is Room => Boolean(room))

        return {
            id: space.id,
            title: space.name,
            collapsed: false,
            rooms,
        }
    })
}

type FilterRoomGroupsArgs = {
    roomGroups: RoomGroup[]
    roomEvents: CalendarEvent[]
    appointmentStart: Date | null
    appointmentEnd: Date | null
    activeTagIds: Set<string>
    minCapacity: number | null
    searchTerm: string
}

export function filterRoomGroups({
    roomGroups,
    roomEvents,
    appointmentStart,
    appointmentEnd,
    activeTagIds,
    minCapacity,
    searchTerm,
}: FilterRoomGroupsArgs) {
    const hasRoomOverlap = (roomId: string) => {
        if (!appointmentStart || !appointmentEnd) {
            return false
        }

        return roomEvents.some((event) => {
            if (!event.reservableID || event.reservableID !== roomId) {
                return false
            }

            const start = new Date(event.startTime)
            const end = new Date(event.endTime)
            return appointmentStart < end && appointmentEnd > start
        })
    }

    const matchesSearchTerm = (room: Room) =>
        searchTerm.length === 0 ||
        normalize(room.name).includes(normalize(searchTerm)) ||
        normalize(room.details).includes(normalize(searchTerm))

    const matchesActiveTags = (room: Room) =>
        activeTagIds.size === 0 || Array.from(activeTagIds).every((tagId) => room.tagIds.includes(tagId))

    const matchesMinCapacity = (room: Room) => minCapacity === null || room.capacity >= minCapacity

    return roomGroups
        .map((group) => ({
            ...group,
            rooms: group.rooms.filter(
                (room) =>
                    matchesSearchTerm(room) &&
                    matchesActiveTags(room) &&
                    matchesMinCapacity(room) &&
                    !hasRoomOverlap(room.id),
            ),
        }))
        .filter((group) => group.rooms.length > 0)
}
